<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import { useTrack } from 'dashboard/composables';
import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';

const props = defineProps({
  conversationId: { type: Number, required: true },
  messageId: { type: Number, required: true },
  hasTranslations: { type: Boolean, default: false },
});

const store = useStore();
const { t } = useI18n();
const uiSettings = useMapGetter('getUISettings');
const getAccount = useMapGetter('accounts/getAccount');
const currentAccountId = useMapGetter('getCurrentAccountId');

const isTranslating = ref(false);

const targetLanguage = computed(
  () =>
    uiSettings.value?.locale ||
    getAccount.value(currentAccountId.value)?.locale ||
    'zh_CN'
);

const handleTranslate = async () => {
  if (props.hasTranslations || isTranslating.value) return;

  isTranslating.value = true;
  try {
    await store.dispatch('translateMessage', {
      conversationId: props.conversationId,
      messageId: props.messageId,
      targetLanguage: targetLanguage.value,
    });
    useTrack(CONVERSATION_EVENTS.TRANSLATE_A_MESSAGE);
  } catch (error) {
    useAlert(parseAPIErrorResponse(error));
  } finally {
    isTranslating.value = false;
  }
};
</script>

<template>
  <button
    v-tooltip.top="t('CONVERSATION.CONTEXT_MENU.TRANSLATE')"
    type="button"
    class="inline-flex items-center justify-center size-7 rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 opacity-0 group-hover/message:opacity-100 transition-opacity disabled:opacity-40"
    :disabled="isTranslating || hasTranslations"
    @click.stop="handleTranslate"
  >
    <Icon
      :icon="isTranslating ? 'i-lucide-loader-2' : 'i-lucide-languages'"
      class="size-3.5"
      :class="{ 'animate-spin': isTranslating }"
    />
  </button>
</template>
